<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const modelValue = defineModel({
  type: Array,
  default: () => [],
});

const { t } = useI18n();
const alert = useAlert;

const isOpen = ref(false);
const isLoading = ref(false);
const isSelectingAll = ref(false);
const selectAllRunId = ref(0);
const autoLoadRunId = ref(0);

const query = ref('');
const ddd = ref('');
const brazilOnly = ref(false);
const page = ref(1);
const hasMore = ref(false);
const totalCount = ref(0);

const rows = ref([]);
const seenPhones = ref(new Set());
const lastFetchToken = ref(0);

const tableScrollRef = ref(null);

const selectedSet = computed(() => new Set(modelValue.value || []));
const selectedCount = computed(() => selectedSet.value.size);

const normalizePhone = phone => (phone || '').toString().replace(/\D/g, '');

const isBrazilPhone = phone => {
  const digits = normalizePhone(phone);
  if (!digits) return false;
  // Accept E.164 BR (+55...) and also local numbers (10/11 digits) as BR.
  if (digits.startsWith('55')) return true;
  return digits.length === 10 || digits.length === 11;
};

const getDDD = phone => {
  const digits = normalizePhone(phone);
  if (!digits) return '';
  if (digits.startsWith('55') && digits.length >= 4) return digits.slice(2, 4);
  if (digits.length >= 2) return digits.slice(0, 2);
  return '';
};

const isSelected = phone => selectedSet.value.has(phone);

const addSelected = phone => {
  const trimmed = (phone || '').toString().trim();
  if (!trimmed) return;

  if (brazilOnly.value && !isBrazilPhone(trimmed)) return;

  if (selectedSet.value.has(trimmed)) return;
  modelValue.value = [...selectedSet.value, trimmed];
};

const removeSelected = phone => {
  modelValue.value = (modelValue.value || []).filter(p => p !== phone);
};

const toggleSelected = phone => {
  if (isSelected(phone)) {
    removeSelected(phone);
  } else {
    addSelected(phone);
  }
};

const clearSelection = () => {
  modelValue.value = [];
};

const removeNonBrazilFromSelection = () => {
  modelValue.value = (modelValue.value || []).filter(p => isBrazilPhone(p));
};

const resetList = () => {
  rows.value = [];
  page.value = 1;
  hasMore.value = false;
  totalCount.value = 0;
  seenPhones.value = new Set();
};

const cancelSelectAll = () => {
  // Bump the run id so any in-flight "select all" loop stops naturally.
  selectAllRunId.value += 1;
  isSelectingAll.value = false;
};

const cancelAutoLoad = () => {
  autoLoadRunId.value += 1;
};

const isFilterActive = computed(() => {
  const wantDDD = (ddd.value || '').toString().trim();
  return brazilOnly.value || Boolean(wantDDD);
});

const fetchPage = async ({ append }) => {
  if (!props.inboxId) return;
  lastFetchToken.value += 1;
  const token = lastFetchToken.value;
  isLoading.value = true;
  try {
    const trimmed = query.value?.trim() || '';
    const response = trimmed
      ? await ContactAPI.search(
          // Match contacts page behavior (encode before passing as q param)
          encodeURIComponent(trimmed),
          page.value,
          'name',
          '',
          null,
          true
        )
      : await ContactAPI.get(page.value, 'name', '', null, true);

    // If a newer request has started, ignore this response.
    if (token !== lastFetchToken.value) return;

    const payload = response?.data?.payload || [];
    const meta = response?.data?.meta || {};
    totalCount.value = Number(meta.count || 0);

    const unique = [];
    payload.forEach(contact => {
      // Prefer the contact_inbox source_id for this inbox (WhatsApp number / wa_id),
      // otherwise fall back to the contact.phone_number when present.
      let phone = '';
      if (contact?.contact_inboxes?.length) {
        const match = contact.contact_inboxes.find(
          ci => Number(ci?.inbox?.id) === Number(props.inboxId)
        );
        phone = (match?.source_id || '').toString().trim();
      }
      if (!phone) {
        phone = (contact?.phone_number || '').toString().trim();
      }
      if (!phone) return;

      if (!phone.startsWith('+')) {
        const digitsOnly = phone.replace(/\D/g, '');
        if (digitsOnly) phone = `+${digitsOnly}`;
      }

      const key = normalizePhone(phone);
      if (!key) return;
      if (seenPhones.value.has(key)) return;

      if (brazilOnly.value && !isBrazilPhone(phone)) return;
      if (ddd.value?.trim()) {
        const want = ddd.value.trim();
        if (want && getDDD(phone) !== want) return;
      }

      seenPhones.value.add(key);
      unique.push({
        id: contact.id,
        name: contact.name,
        phone,
        phoneKey: key,
      });
    });

    rows.value = append ? [...rows.value, ...unique] : unique;

    const currentPage = Number(meta.current_page || page.value);
    // The contacts endpoint meta provides `count` and `current_page` (no total_pages).
    // Chatwoot uses 15 results per page for contacts.
    const RESULTS_PER_PAGE = 15;
    hasMore.value = currentPage * RESULTS_PER_PAGE < totalCount.value;
    page.value = currentPage;

    if (!append) {
      await nextTick();
      if (tableScrollRef.value) tableScrollRef.value.scrollTop = 0;
    }
  } finally {
    if (token === lastFetchToken.value) {
      isLoading.value = false;
    }
  }
};

const autoLoadAllPagesIfFiltered = async () => {
  if (!isOpen.value) return;
  if (!isFilterActive.value) return;
  if (isLoading.value) return;
  if (isSelectingAll.value) return;

  autoLoadRunId.value += 1;
  const runId = autoLoadRunId.value;
  const MAX_PAGES_TO_AUTOLOAD = 200;
  const MAX_NO_PROGRESS_PAGES = 5;
  let pagesLoaded = 0;
  let noProgressStreak = 0;

  while (
    runId === autoLoadRunId.value &&
    isOpen.value &&
    hasMore.value &&
    pagesLoaded < MAX_PAGES_TO_AUTOLOAD
  ) {
    const before = rows.value.length;
    page.value += 1;
    // eslint-disable-next-line no-await-in-loop
    await fetchPage({ append: true });
    pagesLoaded += 1;

    const after = rows.value.length;
    if (after === before) {
      noProgressStreak += 1;
    } else {
      noProgressStreak = 0;
    }

    if (noProgressStreak >= MAX_NO_PROGRESS_PAGES) break;
  }
};

const open = async () => {
  isOpen.value = true;
  resetList();
  if (brazilOnly.value) removeNonBrazilFromSelection();
  await fetchPage({ append: false });
  await autoLoadAllPagesIfFiltered();
};

const close = () => {
  cancelAutoLoad();
  cancelSelectAll();
  isOpen.value = false;
};

const loadMore = async () => {
  if (isLoading.value) return;
  if (isSelectingAll.value) return;
  if (!hasMore.value) return;

  page.value += 1;
  await fetchPage({ append: true });
};

const handleTableScroll = async event => {
  if (!hasMore.value || isLoading.value || isSelectingAll.value) return;
  const el = event?.target;
  if (!el) return;

  const threshold = 64;
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
    await loadMore();
  }
};

const selectAllLoaded = () => {
  const next = new Set(selectedSet.value);
  rows.value.forEach(r => next.add(r.phone));
  modelValue.value = Array.from(next);
};

const selectAllMatching = async () => {
  if (isLoading.value) return;
  if (isSelectingAll.value) return;
  selectAllRunId.value += 1;
  const runId = selectAllRunId.value;
  isSelectingAll.value = true;
  try {
    // Ensure we start from the first page for complete selection.
    resetList();
    await fetchPage({ append: false });

    const next = new Set(selectedSet.value);
    rows.value.forEach(r => next.add(r.phone));

    // Safety cap: selecting tens of thousands into the UI will degrade UX.
    const MAX_SELECT = 5000;
    const MAX_PAGES_TO_SCAN = 200;
    const MAX_NO_PROGRESS_PAGES = 5;
    let pagesScanned = 0;
    let noProgressStreak = 0;

    while (
      runId === selectAllRunId.value &&
      hasMore.value &&
      next.size < MAX_SELECT &&
      pagesScanned < MAX_PAGES_TO_SCAN
    ) {
      const before = next.size;
      page.value += 1;
      // Pagination is sequential because the next page depends on the current result.
      // eslint-disable-next-line no-await-in-loop
      await fetchPage({ append: true });
      rows.value.forEach(r => next.add(r.phone));
      pagesScanned += 1;

      const after = next.size;
      if (after === before) {
        noProgressStreak += 1;
      } else {
        noProgressStreak = 0;
      }

      if (noProgressStreak >= MAX_NO_PROGRESS_PAGES) break;
    }

    if (runId !== selectAllRunId.value) return;
    modelValue.value = Array.from(next);

    if (hasMore.value && next.size >= MAX_SELECT) {
      alert(
        t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SELECT_ALL_LIMIT_REACHED',
          { max: MAX_SELECT }
        )
      );
    }

    if (hasMore.value && pagesScanned >= MAX_PAGES_TO_SCAN) {
      alert(
        t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SELECT_ALL_SCAN_LIMIT_REACHED',
          { maxPages: MAX_PAGES_TO_SCAN }
        )
      );
    }
  } finally {
    if (runId === selectAllRunId.value) {
      isSelectingAll.value = false;
    }
  }
};

watch(
  () => props.inboxId,
  () => {
    if (isOpen.value) {
      resetList();
      fetchPage({ append: false });
    }
  }
);

watch(
  () => query.value,
  debounce(async () => {
    if (!isOpen.value) return;
    cancelSelectAll();
    cancelAutoLoad();
    resetList();
    await fetchPage({ append: false });
    await autoLoadAllPagesIfFiltered();
  }, 350)
);

watch(
  () => brazilOnly.value,
  next => {
    if (next) removeNonBrazilFromSelection();
    if (!isOpen.value) return;
    cancelSelectAll();
    cancelAutoLoad();
    resetList();
    fetchPage({ append: false }).then(autoLoadAllPagesIfFiltered);
  }
);

watch(
  () => ddd.value,
  debounce(async () => {
    if (!isOpen.value) return;
    cancelSelectAll();
    cancelAutoLoad();
    const cleaned = (ddd.value || '').toString().replace(/\D/g, '').slice(0, 2);
    if (cleaned !== ddd.value) ddd.value = cleaned;
    resetList();
    await fetchPage({ append: false });
    await autoLoadAllPagesIfFiltered();
  }, 250)
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between gap-2">
      <div class="text-sm text-n-slate-12">
        {{
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SELECTED', {
            count: selectedCount,
          })
        }}
      </div>
      <div class="flex gap-2">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.CLEAR')"
          :disabled="selectedCount === 0"
          @click="clearSelection"
        />
        <Button
          size="sm"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.OPEN')"
          @click="open"
        />
      </div>
    </div>

    <div
      v-if="selectedCount"
      class="rounded-lg bg-n-alpha-black2 border border-n-weak p-3 max-h-32 overflow-y-auto"
    >
      <div class="flex flex-wrap gap-2 pr-1">
        <div
          v-for="phone in modelValue"
          :key="phone"
          class="flex items-center gap-2 rounded-lg bg-n-alpha-2 px-3 py-1"
        >
          <span class="text-sm text-n-slate-12">{{ phone }}</span>
          <button
            type="button"
            class="i-lucide-x size-3.5 text-n-slate-11"
            @click="removeSelected(phone)"
          />
        </div>
      </div>
    </div>

    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4"
      data-campaign-ignore-clickaway="true"
      @click.self.stop="close"
    >
      <div
        class="w-full max-w-[60rem] h-[calc(100vh-2rem)] rounded-xl bg-n-background border border-n-weak shadow-xl overflow-hidden"
        data-campaign-ignore-clickaway="true"
        @click.stop
      >
        <div
          class="flex items-center justify-between gap-2 p-4 border-b border-n-weak"
        >
          <div class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.TITLE') }}
          </div>
          <button
            type="button"
            class="i-lucide-x size-5 text-n-slate-11"
            @click.stop="close"
          />
        </div>

        <div class="p-4 flex flex-col gap-3">
          <Input
            v-model="query"
            :label="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SEARCH.LABEL')
            "
            :placeholder="
              t(
                'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SEARCH.PLACEHOLDER'
              )
            "
            name="whatsapp-contacts-search"
            autocomplete="off"
          />

          <div class="grid grid-cols-12 gap-3">
            <div class="col-span-12 sm:col-span-4">
              <Input
                v-model="ddd"
                :label="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.FILTERS.DDD.LABEL'
                  )
                "
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.FILTERS.DDD.PLACEHOLDER'
                  )
                "
                name="whatsapp-contacts-ddd"
                inputmode="numeric"
                autocomplete="off"
              />
            </div>
            <div class="col-span-12 sm:col-span-8 flex items-end gap-3">
              <label
                class="flex items-center gap-2 text-sm text-n-slate-12 select-none"
              >
                <input v-model="brazilOnly" type="checkbox" class="size-4" />
                {{
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.FILTERS.BR_ONLY.LABEL'
                  )
                }}
              </label>
            </div>
          </div>

          <div
            v-if="query.trim() && !isLoading && rows.length"
            class="rounded-xl border border-n-weak bg-n-background overflow-hidden"
          >
            <button
              v-for="row in rows.slice(0, 6)"
              :key="`suggest-${row.phoneKey}`"
              type="button"
              class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left border-b border-n-weak last:border-b-0 hover:bg-n-alpha-black2"
              @click="toggleSelected(row.phone)"
            >
              <div class="min-w-0">
                <div class="text-sm text-n-slate-12 truncate">
                  {{ row.name || '-' }}
                </div>
                <div class="text-xs text-n-slate-11 truncate">
                  {{ row.phone }}
                </div>
              </div>
              <span
                v-if="isSelected(row.phone)"
                class="i-lucide-check size-4 text-n-slate-11"
              />
            </button>
          </div>

          <div class="flex items-center justify-between gap-2">
            <div class="text-xs text-n-slate-11">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.DEDUPE_INFO')
              }}
            </div>
            <div class="flex items-center gap-2">
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SELECT_ALL_LOADED'
                  )
                "
                :disabled="rows.length === 0 || isSelectingAll"
                @click="selectAllLoaded"
              />
              <Button
                variant="faded"
                color="slate"
                size="sm"
                :label="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.SELECT_ALL_MATCHING'
                  )
                "
                :disabled="isSelectingAll"
                @click="selectAllMatching"
              />
            </div>
          </div>

          <div class="border border-n-weak rounded-xl overflow-hidden">
            <div
              class="grid grid-cols-12 bg-n-alpha-black2 px-3 py-2 text-xs text-n-slate-11"
            >
              <div class="col-span-1" />
              <div class="col-span-6">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.TABLE.NAME')
                }}
              </div>
              <div class="col-span-5">
                {{
                  t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.TABLE.PHONE')
                }}
              </div>
            </div>
            <div v-if="rows.length === 0" class="p-6 text-sm text-n-slate-11">
              {{
                isLoading
                  ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.LOADING')
                  : t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.EMPTY')
              }}
            </div>
            <div
              v-else
              ref="tableScrollRef"
              class="max-h-[22rem] overflow-y-auto"
              @scroll.passive="handleTableScroll"
            >
              <div
                v-for="row in rows"
                :key="row.phoneKey"
                class="grid grid-cols-12 items-center px-3 py-2 border-t border-n-weak"
              >
                <div class="col-span-1">
                  <input
                    type="checkbox"
                    class="size-4"
                    :checked="isSelected(row.phone)"
                    @change="toggleSelected(row.phone)"
                  />
                </div>
                <div class="col-span-6 text-sm text-n-slate-12 truncate">
                  {{ row.name || '-' }}
                </div>
                <div class="col-span-5 text-sm text-n-slate-12 truncate">
                  {{ row.phone }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2">
            <div class="text-xs text-n-slate-11">
              {{
                t(
                  'CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.PAGINATION_INFO',
                  { loaded: rows.length }
                )
              }}
            </div>
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.LOAD_MORE')
              "
              :disabled="!hasMore || isLoading || isSelectingAll"
              @click="loadMore"
            />
          </div>

          <div class="flex justify-end gap-3 pt-2">
            <Button
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SAVED_CONTACTS.CLOSE')"
              @click="close"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
